/**
 * Pure helpers + constants for the 2D scatter plot.
 * No React, no DOM — math + formatting + per-pillar factor selection only.
 */

import type { ScoredStock, StockFactors, PillarScores } from './compute'
import { PILLAR_FACTORS, isBankLike } from './compute'

// ─── Plot geometry ──────────────────────────────────────────────────────────

export const PADDING = { top: 44, right: 44, bottom: 56, left: 68 }
export const RANGE = 3.2
export const GRID_CELLS = 6
export const CELL_STEP = (RANGE * 2) / GRID_CELLS
export const MAX_PINS = 5

export interface PlotStock extends ScoredStock {
  px: number
  py: number
}

export interface Rect { x: number; y: number; w: number; h: number }

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
}

// ─── Color scales ───────────────────────────────────────────────────────────

// Q–V quadrants. Y axis = valuation (low at bottom = cheap = green-ish bias).
export const CORNERS = {
  topRight:    [59, 130, 246],   // blue — high quality + expensive
  topLeft:     [168, 85, 247],   // purple — low quality + expensive
  bottomRight: [22, 163, 74],    // emerald — high quality + cheap
  bottomLeft:  [202, 138, 4],    // amber — low quality + cheap
}

// Q–M quadrants. Y axis = momentum (high at top). Best = top-right (green); worst = bottom-left (red).
export const CORNERS_QM = {
  topRight:    [22, 163, 74],    // emerald — high quality + high momentum (BEST)
  topLeft:     [168, 85, 247],   // purple — low quality + high momentum (speculative)
  bottomRight: [202, 138, 4],    // amber — high quality + low momentum (value trap)
  bottomLeft:  [239, 68, 68],    // red — low quality + low momentum (AVOID)
}

/** Bilinear interpolation over the 4 corner colors. tx,ty in [0,1]. */
export function lerpColor(
  tl: number[], tr: number[], bl: number[], br: number[], tx: number, ty: number,
): string {
  const r = Math.round(tl[0] * (1 - tx) * ty + tr[0] * tx * ty + bl[0] * (1 - tx) * (1 - ty) + br[0] * tx * (1 - ty))
  const g = Math.round(tl[1] * (1 - tx) * ty + tr[1] * tx * ty + bl[1] * (1 - tx) * (1 - ty) + br[1] * tx * (1 - ty))
  const b = Math.round(tl[2] * (1 - tx) * ty + tr[2] * tx * ty + bl[2] * (1 - tx) * (1 - ty) + br[2] * tx * (1 - ty))
  return `rgb(${r},${g},${b})`
}

// ─── Backtest heatmap cell color ────────────────────────────────────────────

export const FALLBACK_BENCHMARK = 0.689
export const COLOR_CAP = 0.50

/**
 * Diverging color for a backtest grid cell. Returns null for low-confidence
 * cells (n<3) or missing data — caller should not paint those.
 */
export function backtestCellColor(
  ret: number | null, count: number, _benchmark: number,
): string | null {
  if (ret === null || count < 3) return null
  const capped = Math.max(-COLOR_CAP, Math.min(COLOR_CAP, ret))
  const t = (capped + COLOR_CAP) / (COLOR_CAP * 2)
  if (t >= 0.5) {
    const p = (t - 0.5) * 2
    return `rgba(34, 197, 94, ${(p * 0.45).toFixed(2)})`
  }
  const p = 1 - t * 2
  return `rgba(239, 68, 68, ${(p * 0.45).toFixed(2)})`
}

// ─── Dot/label sizing by market cap ────────────────────────────────────────

export function dotRadius(mktCap: number, highlighted: boolean, hovered: boolean, pinned: boolean): number {
  if (highlighted || pinned) return 10
  if (hovered) return 7
  if (mktCap >= 500e9) return 5
  if (mktCap >= 100e9) return 4
  if (mktCap >= 30e9) return 3.2
  return 2.5
}

export function labelSize(mktCap: number, highlighted: boolean, hovered: boolean, pinned: boolean): number {
  if (highlighted || pinned) return 12
  if (hovered) return 11
  if (mktCap >= 500e9) return 9.5
  if (mktCap >= 100e9) return 8
  if (mktCap >= 30e9) return 6.5
  return 5.5
}

export function labelOpacity(mktCap: number, highlighted: boolean, hovered: boolean, pinned: boolean): number {
  if (highlighted || hovered || pinned) return 1
  if (mktCap >= 500e9) return 0.92
  if (mktCap >= 100e9) return 0.72
  if (mktCap >= 30e9) return 0.5
  return 0.32
}

export function mktCapWeight(mktCap: number): number {
  if (mktCap >= 500e9) return 600
  if (mktCap >= 100e9) return 500
  return 400
}

// ─── Score / factor formatting ─────────────────────────────────────────────

export function fmtScore(v: number | null): string {
  if (v === null) return '--'
  return v.toFixed(2)
}

export function fmtFactorVal(v: number | null | undefined, key: string): string {
  if (v === null || v === undefined) return '--'
  const pctKeys = ['roe', 'roa', 'roic', 'grossMargin', 'netMargin', 'fwdEpsGrowth', 'fwdRevGrowth',
    'fwdEpsCagr2yr', 'fwdRevCagr2yr', 'epsCAGR3yr', 'fcfGrowth3yr', 'grossMarginExpansion3yr', 'opMarginExpansion3yr',
    'revenueGrowth3yr', 'nim', 'efficiencyRatio', 'provisionsToNII', 'equityToAssets', 'retainedEarningsToAssets',
    'netDebtPaydownYield', 'dividendYield', 'buybackYield']
  if (pctKeys.includes(key)) return (v * 100).toFixed(1) + '%'
  if (key === 'piotroski') return `${v}/9`
  if (key === 'beta') return v.toFixed(2)
  if (key === 'altmanZ' || key === 'epsVariability') return v.toFixed(1)
  return v.toFixed(1) + 'x'
}

// ─── Time periods (backtest grid columns) ──────────────────────────────────

export const PERIODS = [
  { key: '1yr', label: '1Y' },
  { key: '3yr', label: '3Y' },
  { key: '5yr', label: '5Y' },
] as const

export type PeriodKey = '1yr' | '3yr' | '5yr'

// ─── Drilldown factor selection (peer-group aware) ─────────────────────────

export type DrilldownTarget = Exclude<keyof PillarScores, 'momentum'> | 'valuation' | null

export function unionByKey(
  a: readonly { key: keyof StockFactors; invert: boolean }[],
  b: readonly { key: keyof StockFactors; invert: boolean }[],
): { key: keyof StockFactors; invert: boolean }[] {
  const seen = new Set<string>()
  const out: { key: keyof StockFactors; invert: boolean }[] = []
  for (const f of [...a, ...b]) {
    if (!seen.has(f.key)) { seen.add(f.key); out.push({ key: f.key, invert: f.invert }) }
  }
  return out
}

/**
 * Peer-group-aware drilldown: union the non-financial and financial factor lists
 * per pillar so both bank and non-bank pinned stocks show their relevant factors.
 * Banks-only → financial list, non-banks-only → non-financial, mixed → union.
 */
export function buildDrillFactors(
  drilldown: 'profitability' | 'growth' | 'safety' | 'shareholderReturns' | 'valuation',
  pinnedStocks: ScoredStock[],
): { key: keyof StockFactors; invert: boolean }[] {
  const hasBank = pinnedStocks.some(s => isBankLike(s))
  const hasNonBank = pinnedStocks.some(s => !isBankLike(s))
  if (drilldown === 'valuation') {
    const nonFin = PILLAR_FACTORS.nonFinancial.valuation
    const fin = PILLAR_FACTORS.financial.valuation
    if (hasBank && !hasNonBank) return fin
    if (hasNonBank && !hasBank) return nonFin
    return unionByKey(nonFin, fin)
  }
  const nonFin = PILLAR_FACTORS.nonFinancial[drilldown] ?? []
  const fin = PILLAR_FACTORS.financial[drilldown] ?? []
  if (hasBank && !hasNonBank) return fin
  if (hasNonBank && !hasBank) return nonFin
  return unionByKey(nonFin, fin)
}
